import {useInfiniteQuery, UseInfiniteQueryOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {QUERY_KEYS} from '@/queryClient'
import {ActivityLogsQueryParams, EntityUrlType} from '@/features/entity-activity-logs/types'
import {infiniteQueryFetchNextPage} from '@/utilities/helpers'
import {
    HttpGetEntityActivityLogsResponse,
    httpGetEntityActivityLogs
} from '@/features/entity-activity-logs/http/entityActivityLogs.http'

interface UseGetEntityActivityLogsProps {
    entityType: EntityUrlType
    entityId?: string
    queryParams?: Omit<ActivityLogsQueryParams, 'page'>
    options?: Pick<UseInfiniteQueryOptions<HttpGetEntityActivityLogsResponse, AxiosError>, 'enabled'>
}

export const useGetEntityActivityLogs = ({
    entityType,
    entityId,
    queryParams,
    options
}: UseGetEntityActivityLogsProps) => {
    const query = useInfiniteQuery<HttpGetEntityActivityLogsResponse, AxiosError>({
        queryKey: [QUERY_KEYS.ENTITY_ACTIVITY_LOGS, entityType, entityId, queryParams],
        queryFn: ({pageParam = 1}) =>
            httpGetEntityActivityLogs({
                urlParams: {
                    entityType,
                    entityId: entityId!
                },
                params: {
                    page: pageParam as number,
                    ...queryParams
                }
            }),
        enabled: !!entityId && options?.enabled !== false,
        initialPageParam: 1,
        getNextPageParam: ({meta}) => (meta.currentPage < meta.lastPage ? meta.currentPage + 1 : null),
        ...options
    })

    return {
        ...query,
        fetchNextPage: () => infiniteQueryFetchNextPage(query),
        remappedData: query.data?.pages[0]?.data?.length ? query.data.pages.flatMap(page => page.data) : []
    }
}
